class Osmocam0908CloserLook extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.mediaIntent = 0;
    this.mediaState = new WeakMap();
    this.grid = this.querySelector('.osmocam-0908-closer-look__grid');
    this.scrollbar = this.querySelector('.osmocam-0908-closer-look__scrollbar');
    this.scrollbarThumb = this.querySelector('.osmocam-0908-closer-look__scrollbar-thumb');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = this.syncMotion.bind(this);
    this.onGridKeydown = this.handleGridKeydown.bind(this);
    this.onGridScroll = this.updateScrollIndicator.bind(this);
    this.onWindowResize = this.updateScrollIndicator.bind(this);
    this.onIntersection = this.handleIntersection.bind(this);
    this.mediaObserver = new IntersectionObserver(this.onIntersection, { rootMargin: '200px 0px' });
    this.motionQuery.addEventListener('change', this.onMotionChange);
    this.grid?.addEventListener('keydown', this.onGridKeydown);
    this.grid?.addEventListener('scroll', this.onGridScroll, { passive: true });
    window.addEventListener('resize', this.onWindowResize);
    this.querySelectorAll('.osmocam-0908-closer-look__card').forEach((card) => this.mediaObserver.observe(card));
    this.syncMotion();
    requestAnimationFrame(this.onGridScroll);
  }

  disconnectedCallback() {
    if (this.motionQuery && this.onMotionChange) {
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    }
    this.grid?.removeEventListener('keydown', this.onGridKeydown);
    this.grid?.removeEventListener('scroll', this.onGridScroll);
    window.removeEventListener('resize', this.onWindowResize);
    this.mediaObserver?.disconnect();
    this.mediaIntent += 1;
    this.querySelectorAll('video').forEach((video) => video.pause());
    this.mediaState = new WeakMap();
    this.grid = null;
    this.scrollbar = null;
    this.scrollbarThumb = null;
    this.mediaObserver = null;
    this.motionQuery = null;
    this.initialized = false;
  }

  updateScrollIndicator() {
    if (!this.grid || !this.scrollbar || !this.scrollbarThumb) return;

    const scrollableWidth = this.grid.scrollWidth - this.grid.clientWidth;
    if (scrollableWidth <= 1) {
      this.scrollbar.hidden = true;
      return;
    }

    this.scrollbar.hidden = false;
    const trackWidth = this.scrollbar.clientWidth;
    if (trackWidth <= 0) return;

    const viewportRatio = Math.min(1, this.grid.clientWidth / this.grid.scrollWidth);
    const thumbWidth = Math.min(trackWidth, Math.max(48, trackWidth * viewportRatio));
    const thumbOffset = (this.grid.scrollLeft / scrollableWidth) * (trackWidth - thumbWidth);

    this.scrollbarThumb.style.width = `${thumbWidth}px`;
    this.scrollbarThumb.style.transform = `translate3d(${thumbOffset}px, 0, 0)`;
  }

  syncMotion() {
    if (!this.initialized) return;
    if (this.motionQuery?.matches) {
      this.mediaIntent += 1;
      this.querySelectorAll('video').forEach((video) => {
        video.pause();
        const deferredMedia = video.closest('deferred-media');
        const state = deferredMedia && this.mediaState.get(deferredMedia);
        if (!state) return;
        state.loaded = true;
        state.video = video;
        state.wantsPlayback = false;
      });
      return;
    }

    this.querySelectorAll('.osmocam-0908-closer-look__card').forEach((card) => {
      const bounds = card.getBoundingClientRect();
      if (bounds.bottom >= -200 && bounds.top <= window.innerHeight + 200) this.startCardMedia(card);
    });
  }

  handleGridKeydown(event) {
    if (!this.grid || this.grid.scrollWidth <= this.grid.clientWidth) return;

    const { key } = event;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;

    event.preventDefault();
    const behavior = this.motionQuery?.matches ? 'auto' : 'smooth';
    if (key === 'Home' || key === 'End') {
      this.grid.scrollTo({
        left: key === 'Home' ? 0 : this.grid.scrollWidth - this.grid.clientWidth,
        behavior,
      });
      return;
    }

    const firstItem = this.grid.querySelector('.osmocam-0908-closer-look__item');
    const step = firstItem?.getBoundingClientRect().width || this.grid.clientWidth;
    this.grid.scrollBy({ left: key === 'ArrowLeft' ? -step : step, behavior });
  }

  handleIntersection(entries) {
    if (!this.initialized || this.motionQuery?.matches) return;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.startCardMedia(entry.target);
      this.mediaObserver?.unobserve(entry.target);
    });
  }

  startCardMedia(card) {
    if (this.motionQuery?.matches) return;

    const deferredMedia = Array.from(card.querySelectorAll('deferred-media')).find((media) => {
      const container = media.closest('.osmocam-0908-closer-look__media-desktop, .osmocam-0908-closer-look__media-mobile, .osmocam-0908-closer-look__media-shared');
      return !container || getComputedStyle(container).display !== 'none';
    });
    if (!deferredMedia) return;

    const state = this.mediaState.get(deferredMedia) || {
      loadStarted: false,
      loaded: false,
      wantsPlayback: false,
      video: null,
    };
    this.mediaState.set(deferredMedia, state);

    let video = deferredMedia.querySelector('video');
    if (!video && !state.loadStarted) {
      const playButton = deferredMedia.querySelector('[ref="deferredMediaPlayButton"]');
      if (!playButton) return;
      deferredMedia.querySelector('template')?.content.querySelector('video')?.removeAttribute('autoplay');
      state.loadStarted = true;
      playButton.click();
      video = deferredMedia.querySelector('video');
    }
    if (!video) return;

    state.loaded = true;
    state.video = video;
    state.wantsPlayback = true;
    const mediaIntent = ++this.mediaIntent;
    const playPromise = video.play();
    playPromise?.catch(() => {
      if (this.mediaIntent !== mediaIntent) return;
      const currentState = this.mediaState.get(deferredMedia);
      if (currentState?.video === video) currentState.wantsPlayback = false;
    });
  }
}

if (!customElements.get('osmocam-0908-closer-look')) {
  customElements.define('osmocam-0908-closer-look', Osmocam0908CloserLook);
}
